import { useState, useEffect } from 'react';
import { SmartphoneIcon, MailIcon, BellIcon, MonitorIcon, HelpCircleIcon, ChevronDown, Loader2 } from 'lucide-react';
import { usePostNotificationsUserPreferencesMutation, useGetNotificationsUserPreferencesQuery } from '@/services';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { toast } from '../ui/toaster';
import { Skeleton } from '@/components/ui/skeleton';

const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

export interface Preference {
  pk?: number | null;
  user_pk: number;
  tenant_pk: number;
  event_type_pk: number;
  deleted_at?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  enabled: boolean;
  channels: {
    sms: boolean;
    push: boolean;
    email: boolean;
    webapp: boolean;
  };
  quiet_hours_start?: string | null;
  quiet_hours_end?: string | null;
  quiet_hours_timezone?: string | null;
  event_type_id: string;
  event_type_name: string;
  event_type_code: string;
  event_type_description: string;
  event_type_category: string;
  event_type_is_default_enabled: boolean;
}

export function NotificationsPreferences() {
  const [saving, setSaving] = useState(false);
  const [preferences, setPreferences] = useState<Preference[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [expandedItems, setExpandedItems] = useState<Record<string, boolean>>({});
  const channelKeys = ['sms', 'push', 'email', 'webapp'] as const;
  const [alterUserPreferences] = usePostNotificationsUserPreferencesMutation();

  function PreferencesSkeleton() {
    return (
      <div className="w-full">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="flex w-full items-center space-x-4 py-4">
            <Skeleton className="h-6 w-1/4" />
            <Skeleton className="h-6 w-1/3" />
            <Skeleton className="h-8 w-24 ml-auto" />
          </div>
        ))}
      </div>
    );
  }
  const toggleExpanded = (code: string) => {
    setExpandedItems((prev) => ({
      ...prev,
      [code]: !prev[code],
    }));
  };

  const { data: preferencesData, isLoading, refetch: refetchPreferences } = useGetNotificationsUserPreferencesQuery({});

  useEffect(() => {
    if (preferencesData?.data) {
      setPreferences(preferencesData.data);

      const firstCategory = preferencesData.data[0]?.event_type_category;
      if (firstCategory) {
        setSelectedCategory(firstCategory);
      }
    }
  }, [preferencesData]);

  const categorizedData: Record<string, Preference[]> = {};
  preferences.forEach((pref) => {
    const category = pref.event_type_category;
    if (!categorizedData[category]) {
      categorizedData[category] = [];
    }
    categorizedData[category].push(pref);
  });

  const [form, setForm] = useState<{ preferences: any[] }>({ preferences: [] });

  const togglePreference = (event_type_code: string, checked: boolean) => {
    setPreferences((prev) =>
      prev.map((pref) => (pref.event_type_code === event_type_code ? { ...pref, enabled: checked } : pref)),
    );

    const original = preferences.find((p) => p.event_type_code === event_type_code);
    if (!original) return;

    const updatedPref = {
      ...original,
      enabled: checked,
    };

    setForm((prevForm) => {
      const exists = prevForm.preferences.find((p) => p.event_type_code === event_type_code);
      let updatedList;

      if (exists) {
        updatedList = prevForm.preferences.map((p) => (p.event_type_code === event_type_code ? updatedPref : p));
      } else {
        updatedList = [...prevForm.preferences, updatedPref];
      }

      return { preferences: updatedList };
    });
  };

  const handleChannelToggle = (pref: Preference, channelKey: string, checked: boolean) => {
    setPreferences((prev) =>
      prev.map((p) =>
        p.event_type_code === pref.event_type_code
          ? {
              ...p,
              channels: {
                ...p.channels,
                [channelKey]: checked,
              },
            }
          : p,
      ),
    );

    setForm((prevForm) => {
      const exists = prevForm.preferences.find((p) => p.event_type_code === pref.event_type_code);

      const updatedChannels = {
        ...pref.channels,
        [channelKey]: checked,
      };

      const updatedPref = {
        ...pref,
        channels: updatedChannels,
      };

      let updatedList;
      if (exists) {
        updatedList = prevForm.preferences.map((p) => (p.event_type_code === pref.event_type_code ? updatedPref : p));
      } else {
        updatedList = [...prevForm.preferences, updatedPref];
      }

      return { preferences: updatedList };
    });
  };

  const handleTimePreferenceChange = (
    event_type_code: string,
    field: 'quiet_hours_start' | 'quiet_hours_end',
    value: string,
  ) => {
    setPreferences((prev) =>
      prev.map((pref) =>
        pref.event_type_code === event_type_code ? { ...pref, [field]: value, quiet_hours_timezone: timezone } : pref,
      ),
    );

    const original = preferences.find((p) => p.event_type_code === event_type_code);
    if (!original) return;

    const updatedPref = {
      ...original,
      [field]: value,
      quiet_hours_timezone: timezone,
    };

    setForm((prevForm) => {
      const exists = prevForm.preferences.find((p) => p.event_type_code === event_type_code);
      let updatedList;

      if (exists) {
        updatedList = prevForm.preferences.map((p) => (p.event_type_code === event_type_code ? updatedPref : p));
      } else {
        updatedList = [...prevForm.preferences, updatedPref];
      }

      return { preferences: updatedList };
    });
  };

  const handleSave = async () => {
    setSaving(true);
    let response;
    try {
      response = await alterUserPreferences({ body: form.preferences });

      if (!response || !response.data?.success) {
        toast.error('Failed to save configuration.');
        return;
      }

      toast.success('Configuration saved successfully.');
    } catch (e) {
      toast.error('Something went wrong.');
      return;
    } finally {
      setSaving(false);
      await refetchPreferences();
    }
  };

  function channelIcons(channelKey: string) {
    switch (channelKey) {
      case 'sms':
        return <SmartphoneIcon className="w-4 h-4" />;
      case 'email':
        return <MailIcon className="w-4 h-4" />;
      case 'push':
        return <BellIcon className="w-4 h-4" />;
      case 'webapp':
        return <MonitorIcon className="w-4 h-4" />;
      default:
        return <HelpCircleIcon className="w-4 h-4 text-gray-400" />;
    }
  }

  const categories = Object.keys(categorizedData);

  return (
    <div className="space-y-5 w-full max-w-6xl mx-auto">
      <h2 className="text-xl font-semibold px-1">Notification Preferences</h2>
      {isLoading ? (
        <PreferencesSkeleton />
      ) : (
        <Tabs value={selectedCategory ?? ''} onValueChange={setSelectedCategory} className="w-full">
          <div className="overflow-x-auto pb-2">
            <TabsList
              className={`inline-flex h-10 items-center justify-center rounded-md bg-muted p-1 text-muted-foreground min-w-full lg:min-w-0`}
            >
              {categories.map((category) => (
                <TabsTrigger
                  key={category}
                  value={category}
                  className="capitalize whitespace-nowrap px-3 py-1.5 text-sm font-medium transition-all"
                >
                  {category}
                </TabsTrigger>
              ))}
            </TabsList>
          </div>

          {categories.map((category) => (
            <TabsContent key={category} value={category}>
              <Card className="p-4 space-y-4 mt-4">
                {categorizedData[category].map((pref) => (
                  <div key={pref.event_type_code} className="border-b pb-2">
                    {/* Main row */}
                    <div className="flex justify-between items-center">
                      <div>
                        <div className="font-medium">{pref.event_type_name}</div>
                        <div className="text-sm text-muted-foreground">{pref.event_type_description}</div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={pref.enabled}
                          onCheckedChange={(checked) => {
                            togglePreference(pref.event_type_code, checked);
                            if (!checked && expandedItems[pref.event_type_code]) {
                              toggleExpanded(pref.event_type_code);
                            }
                          }}
                        />
                        <button
                          onClick={() => toggleExpanded(pref.event_type_code)}
                          disabled={!pref.enabled}
                          aria-expanded={!!expandedItems[pref.event_type_code]}
                          aria-controls={`expand-${pref.event_type_code}`}
                        >
                          <ChevronDown
                            className={`transform transition-transform duration-300 ${
                              expandedItems[pref.event_type_code] ? 'rotate-180' : ''
                            }`}
                          />
                        </button>
                      </div>
                    </div>

                    <div
                      id={`expand-${pref.event_type_code}`}
                      className="overflow-hidden transition-all duration-300 ease-in-out"
                      style={{
                        maxHeight: expandedItems[pref.event_type_code] ? '300px' : '0px',
                      }}
                    >
                      {expandedItems[pref.event_type_code] && (
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-6 p-6 rounded-xl shadow-sm">
                          {channelKeys.map((channelKey) => (
                            <div key={channelKey} className="flex items-center justify-center gap-3">
                              {channelIcons(channelKey)}
                              <span className="text-sm">{channelKey}</span>
                              <Switch
                                checked={pref.channels[channelKey]}
                                onCheckedChange={(checked) => handleChannelToggle(pref, channelKey, checked)}
                              />
                            </div>
                          ))}

                          <div className="flex" style={{ gap: '11rem' }}>
                            {/* Quiet Hour Start */}
                            <div className="flex flex-col text-sm">
                              <label className="font-medium mb-1">Quiet Hour Start</label>
                              <input
                                type="time"
                                step="1"
                                value={pref.quiet_hours_start || ''}
                                onChange={(e) =>
                                  handleTimePreferenceChange(pref.event_type_code, 'quiet_hours_start', e.target.value)
                                }
                                className="border rounded px-2 py-1 "
                                style={{ backgroundColor: '#18241c' }}
                              />
                            </div>

                            {/* Quiet Hour End */}
                            <div className="flex flex-col text-sm">
                              <label className="font-medium mb-1">Quiet Hour End</label>
                              <input
                                type="time"
                                step="1"
                                value={pref.quiet_hours_end || ''}
                                onChange={(e) =>
                                  handleTimePreferenceChange(pref.event_type_code, 'quiet_hours_end', e.target.value)
                                }
                                className="border rounded px-2 py-1 "
                                style={{ backgroundColor: '#18241c' }}
                              />
                            </div>

                            {/* Timezone */}
                            <div className="flex flex-col text-sm">
                              <label className="font-medium mb-1">Timezone</label>
                              <input
                                type="text"
                                value={timezone}
                                readOnly
                                className="border rounded px-2 py-1 cursor-not-allowed"
                                style={{ backgroundColor: '#18241c' }}
                              />
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </Card>
            </TabsContent>
          ))}
        </Tabs>
      )}

      <Button onClick={handleSave} disabled={saving}>
        {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
        {saving ? 'Saving...' : 'Save Configuration'}
      </Button>
    </div>
  );
}
